// Resize-guard primitive for canvas-owning panels (Edit, Map Builder,
// Characters, Animation, FX, Cutscenes - see PANEL_REGISTRY's
// `canvasOwning` flags).
// dockview's default `onlyWhenVisible` panel renderer keeps a hidden tab's
// React tree mounted (content is portalled, not torn down - which is the
// whole point of using dockview over the old `{mode === 'x' && <XRoute/>}`
// switch) but collapses its DOM container to zero size. A naive
// ResizeObserver on that container would then feed the Route's own
// `<Canvas>` (out of this task's reach - see "do not touch the internals of
// any *Route component") a 0x0 resize on every tab switch. See PanelHost.tsx
// for how this hook's output is used: it freezes the container's own CSS
// box at the last known-good size instead, so the Route's internal canvas
// sizing never observes zero.
import { useEffect, useRef, useState, type RefObject } from 'react';

export interface StableSize {
  width: number;
  height: number;
}

/**
 * Tracks an element's size via ResizeObserver, preferring the
 * `device-pixel-content-box` box - falling back to `content-box` (CSS px)
 * where the browser doesn't support it - rather than
 * `clientWidth * devicePixelRatio`. The CSS content-box can round
 * differently from the real backing-store pixel grid under fractional DPR
 * or browser zoom, which is exactly the class of bug behind this org's
 * prior HiDPI canvas-crop incident on Sunder.
 *
 * Zero-size entries are ignored outright: the hook keeps reporting the last
 * known-good size instead of ever surfacing {0, 0}.
 */
export function useDevicePixelSize(ref: RefObject<HTMLElement | null>): StableSize | null {
  const [size, setSize] = useState<StableSize | null>(null);
  const sizeRef = useRef<StableSize | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const commit = (width: number, height: number) => {
      if (width <= 0 || height <= 0) return;
      const current = sizeRef.current;
      if (current && current.width === width && current.height === height) return;
      const next = { width, height };
      sizeRef.current = next;
      setSize(next);
    };

    const handleEntries = (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        const devicePixelBox = (entry as ResizeObserverEntry & { devicePixelContentBoxSize?: readonly ResizeObserverSize[] })
          .devicePixelContentBoxSize?.[0];
        if (devicePixelBox) {
          const dpr = window.devicePixelRatio || 1;
          commit(devicePixelBox.inlineSize / dpr, devicePixelBox.blockSize / dpr);
          continue;
        }
        const contentBox = entry.contentBoxSize?.[0];
        if (contentBox) commit(contentBox.inlineSize, contentBox.blockSize);
        else commit(entry.contentRect.width, entry.contentRect.height);
      }
    };

    const observer = new ResizeObserver(handleEntries);
    try {
      observer.observe(el, { box: 'device-pixel-content-box' });
    } catch {
      // Older Safari throws on an unrecognised `box` option instead of
      // ignoring it; retry with the standard content-box observation.
      observer.observe(el);
    }

    return () => observer.disconnect();
  }, [ref]);

  return size;
}
